/**
 * Fusion — product
 *
 * Variant picker, media gallery, zoom modal, add to cart, quick add, share and
 * lazy-loaded recommendations.
 */

(function () {
  'use strict';

  const strings = window.Fusion.strings;

  /* ======================================================================
     Variant picker
     ====================================================================== */

  /**
   * On selection the section is re-rendered server side and only the affected
   * regions are swapped in. Swapping regions rather than the whole section
   * keeps keyboard focus on the option the customer just chose.
   */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.productUrl = this.dataset.productUrl;
      this.variants = this.readVariants();

      this.addEventListener('change', () => this.onChange());
    }

    readVariants() {
      const script = this.querySelector('[data-variant-data]');
      if (!script) return [];
      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        console.error('[Fusion] Could not parse variant data.', error);
        return [];
      }
    }

    /**
     * Scopes DOM lookups to this picker's own product section. Without this,
     * `document.querySelector` finds the FIRST matching element anywhere on
     * the page, which can be a completely unrelated element when more than
     * one product section is rendered on the same page (e.g. recommendations
     * below the main product). Falls back to `document` for pickers that are
     * not wrapped in a `Product-{sectionId}` container.
     */
    getRoot() {
      return document.getElementById(`Product-${this.sectionId}`) || document;
    }

    /** Selected option values, ordered by option position. */
    get selectedOptions() {
      const inputs = Array.from(this.querySelectorAll('[data-option-input]'));
      const values = [];

      inputs.forEach((input) => {
        const position = parseInt(input.dataset.optionPosition, 10) - 1;
        if (input.tagName === 'SELECT') {
          values[position] = input.value;
        } else if (input.checked) {
          values[position] = input.value;
        }
      });

      return values;
    }

    /** Positions (0-indexed) whose input(s) carry `data-option-auto`. */
    get autoPositions() {
      return new Set(
        Array.from(this.querySelectorAll('[data-option-auto]')).map(
          (input) => parseInt(input.dataset.optionPosition, 10) - 1
        )
      );
    }

    get selectedVariant() {
      const selected = this.selectedOptions;
      return this.variants.find((variant) =>
        variant.options.every((option, i) => option === selected[i])
      );
    }

    onChange() {
      this.resolveAutoOptions();
      this.updateSelectedLabels();
      // Runs regardless of whether the full combination matches a real
      // variant — this is what keeps the OTHER option groups' available/
      // unavailable state in sync the moment the first one changes, rather
      // than only after a server round-trip that itself requires a complete,
      // real variant to succeed (see updateAvailability's own comment).
      this.updateAvailability();

      const variant = this.selectedVariant;

      if (!variant) {
        this.setUnavailable();
        return;
      }

      this.updateUrl(variant);
      this.updateGallery(variant);
      this.renderSection(variant);
    }

    /** Keeps the "Color: Blue" label in sync with the chosen radio. */
    updateSelectedLabels() {
      this.querySelectorAll('[data-option-value]').forEach((label) => {
        const position = label.dataset.optionValue;
        const input = this.querySelector(
          `[data-option-position="${position}"]:checked, select[data-option-position="${position}"]`
        );
        if (input) label.textContent = input.value;
      });
    }

    updateUrl(variant) {
      if (!this.productUrl) return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    updateGallery(variant) {
      const gallery = this.getRoot().querySelector('[data-gallery]');
      if (!gallery || !variant.featured_media) return;
      gallery.select?.(String(variant.featured_media.id));
    }

    setUnavailable() {
      const root = this.getRoot();
      const button = root.querySelector('[data-add-to-cart]');
      const label = root.querySelector('[data-add-to-cart-text]');
      if (!button || !label) return;

      button.disabled = true;
      label.textContent = strings.unavailable;
    }

    async renderSection(variant) {
      const url = `${this.productUrl}?variant=${variant.id}&section_id=${this.sectionId}`;

      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`Variant render failed (${response.status})`);

        const html = new DOMParser().parseFromString(await response.text(), 'text/html');

        // Scope to this product so recommendation cards below are never touched
        const root = this.getRoot();

        // Regions whose content depends on the selected variant. `[data-price]`
        // (rather than a wrapper class like `.product__price`) is what price.liquid
        // itself renders, so this matches everywhere price.liquid is used, not
        // just callers that happen to wrap it in a specific class.
        const regions = [
          '[data-price]',
          '[data-inventory]',
          '[data-pickup]',
          '[data-product-sku]',
          'product-form',
        ];

        regions.forEach((selector) => {
          const fresh = html.querySelector(selector);
          const existing = root.querySelector(selector);
          if (!fresh || !existing) return;

          // Replace the element rather than its innerHTML. `product-form` is a
          // custom element holding a reference to its <form>; overwriting the
          // innerHTML would leave it bound to a detached node and silently
          // break add to cart. replaceWith lets the new element upgrade and
          // rebind itself.
          existing.replaceWith(fresh);
        });

        document.dispatchEvent(
          new CustomEvent('variant:change', { detail: { variant }, bubbles: true })
        );
      } catch (error) {
        console.error('[Fusion] Variant update failed.', error);
      }
    }

    /**
     * Recomputes which option values are still selectable, purely from the
     * variants JSON already on the page — no server round-trip involved.
     *
     * This matters because the old approach only refreshed availability as a
     * side effect of successfully rendering a fully-matched variant. Picking
     * a new value in one option group very often leaves the OTHER groups
     * pointed at a combination that no longer exists (e.g. switching Color
     * while the previously-selected Ink Color isn't offered for it) — that
     * has no matching variant at all, so the render step never ran and the
     * other group's choices were left showing stale availability. Computing
     * it directly from `this.variants` works regardless of whether the
     * current combination happens to resolve to a real variant.
     *
     * Auto-display positions (e.g. Capacity) are excluded on BOTH sides of
     * this: their own inputs never get an unavailable state shown (they have
     * no visible choice UI to show it on anyway), and — importantly — their
     * currently resolved value is never used as a constraint when checking
     * whether some OTHER position's value is available. Without that second
     * part, picking a new Model could wrongly cross out Color options simply
     * because the previous Capacity doesn't exist for that Model, even
     * though Capacity was never something the shopper chose.
     */
    updateAvailability() {
      const autoPositions = this.autoPositions;
      const selected = this.selectedOptions.map((value, i) => (autoPositions.has(i) ? null : value));

      this.querySelectorAll('[data-option-input]').forEach((input) => {
        const position = parseInt(input.dataset.optionPosition, 10) - 1;
        if (autoPositions.has(position)) return;

        if (input.tagName === 'SELECT') {
          Array.from(input.options).forEach((opt) => {
            if (!opt.value) return;
            const available = this.hasAvailableVariant(position, opt.value, selected);
            opt.disabled = !available;
            opt.toggleAttribute('data-unavailable', !available);
          });
          return;
        }

        const available = this.hasAvailableVariant(position, input.value, selected);
        input.toggleAttribute('data-unavailable', !available);

        const label = this.querySelector(`label[for="${input.id}"]`);
        label?.classList.toggle('is-unavailable', !available);
      });
    }

    /**
     * True if some variant has `value` at `position` and — for every OTHER
     * position that already has a selection — matches that too. Positions
     * with no selection yet (nothing checked/selected, or nulled out by the
     * caller — see updateAvailability) are ignored, so this works correctly
     * on the very first pick as well as every pick after.
     */
    hasAvailableVariant(position, value, selected) {
      return this.variants.some(
        (variant) =>
          variant.available &&
          variant.options[position] === value &&
          variant.options.every(
            (option, i) => i === position || selected[i] == null || option === selected[i]
          )
      );
    }

    /**
     * Options flagged auto-display (e.g. "Capacity") are never offered as a
     * choice — the shopper only picks the OTHER options. This fills in the
     * implied value for the auto option before matching runs, so
     * `selectedVariant` (which requires every position to line up exactly)
     * keeps working, and the badge showing the resolved value stays correct
     * as Model/Color change.
     */
    resolveAutoOptions() {
      const positions = this.autoPositions;
      if (!positions.size) return;

      const autoInputs = Array.from(this.querySelectorAll('[data-option-auto]'));
      const selected = this.selectedOptions;

      positions.forEach((position) => {
        const inputs = autoInputs.filter(
          (input) => parseInt(input.dataset.optionPosition, 10) - 1 === position
        );

        const stillValid = inputs.some(
          (input) => input.checked && this.hasAvailableVariant(position, input.value, selected)
        );
        if (stillValid) return;

        const next = inputs.find((input) => this.hasAvailableVariant(position, input.value, selected));
        if (next) next.checked = true;
      });
    }
  }

  if (!customElements.get('variant-picker')) {
    customElements.define('variant-picker', VariantPicker);
  }

  /* ======================================================================
     Media gallery
     ====================================================================== */

  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
      this.thumbnails = Array.from(this.querySelectorAll('[data-gallery-thumbnail]'));
      this.status = this.querySelector('[data-gallery-status]');

      this.thumbnails.forEach((thumb) => {
        thumb.addEventListener('click', () => this.select(thumb.dataset.mediaId));
      });

      // Arrow keys move through the thumbnail strip
      this.querySelector('[data-gallery-thumbnails]')?.addEventListener('keydown', (event) => {
        const keys = ['ArrowLeft', 'ArrowRight'];
        if (!keys.includes(event.key)) return;

        event.preventDefault();
        const index = this.thumbnails.findIndex((t) => t === document.activeElement);
        if (index === -1) return;

        const offset = event.key === 'ArrowRight' ? 1 : -1;
        const next = this.thumbnails[(index + offset + this.thumbnails.length) % this.thumbnails.length];
        next.focus();
        this.select(next.dataset.mediaId);
      });

      this.querySelectorAll('[data-gallery-zoom]').forEach((button) => {
        button.addEventListener('click', () => {
          document.querySelector('[data-media-modal]')?.open(
            button.dataset.zoomSrc,
            button.dataset.zoomAlt
          );
        });
      });

      this.querySelectorAll('[data-video-trigger]').forEach((button) => {
        button.addEventListener('click', () => this.loadVideo(button));
      });

      this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.step(-1));
      this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.step(1));
    }

    /** Moves to the previous/next slide, wrapping around at either end. */
    step(offset) {
      if (this.slides.length < 2) return;

      const currentIndex = this.slides.findIndex((slide) => slide.classList.contains('is-active'));
      const nextIndex = (currentIndex + offset + this.slides.length) % this.slides.length;
      this.select(this.slides[nextIndex].dataset.mediaId);
    }

    select(mediaId) {
      if (!mediaId) return;

      let index = 0;

      this.slides.forEach((slide, i) => {
        const active = slide.dataset.mediaId === mediaId;
        slide.classList.toggle('is-active', active);
        slide.toggleAttribute('aria-hidden', !active);
        if (active) index = i;

        // Stop any playing video when moving away from its slide
        if (!active) slide.querySelector('video')?.pause();
      });

      this.thumbnails.forEach((thumb) => {
        const active = thumb.dataset.mediaId === mediaId;
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-current', String(active));
      });

      // Carousel and stacked layouts scroll rather than swap
      if (this.dataset.layout === 'carousel' || this.dataset.layout === 'stacked') {
        this.slides[index]?.scrollIntoView({
          behavior: window.Fusion.motionAllowed() ? 'smooth' : 'auto',
          block: 'nearest',
          inline: 'start',
        });
      }

      if (this.status) {
        this.status.textContent = `Image ${index + 1} of ${this.slides.length}`;
      }
    }

    /** Builds the embed only once the customer asks for it. */
    loadVideo(button) {
      const { videoHost, videoId } = button.dataset;

      const src =
        videoHost === 'youtube'
          ? `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`
          : `https://player.vimeo.com/video/${videoId}?autoplay=1`;

      const iframe = document.createElement('iframe');
      iframe.src = src;
      iframe.className = 'gallery__iframe';
      iframe.allow = 'autoplay; encrypted-media; picture-in-picture';
      iframe.allowFullscreen = true;
      iframe.title = button.textContent.trim();

      button.replaceWith(iframe);
    }
  }

  if (!customElements.get('media-gallery')) {
    customElements.define('media-gallery', MediaGallery);
  }

  /* ======================================================================
     Zoom modal
     ====================================================================== */

  class FusionMediaModal extends HTMLElement {
    connectedCallback() {
      this.image = this.querySelector('[data-media-modal-image]');
      this.onKeydown = this.onKeydown.bind(this);

      this.querySelectorAll('[data-media-modal-close]').forEach((el) => {
        el.addEventListener('click', () => this.close());
      });
    }

    open(src, alt) {
      if (!src) return;

      this.image.src = src;
      this.image.alt = alt || '';

      this.removeAttribute('hidden');
      window.Fusion.lockScroll();
      this.releaseFocus = window.Fusion.trapFocus(this, this.querySelector('[data-media-modal-close]'));
      document.addEventListener('keydown', this.onKeydown);
    }

    close() {
      this.setAttribute('hidden', '');
      window.Fusion.unlockScroll();
      this.releaseFocus?.();
      this.releaseFocus = null;
      document.removeEventListener('keydown', this.onKeydown);

      // Release the decoded image from memory
      this.image.removeAttribute('src');
    }

    onKeydown(event) {
      if (event.key === 'Escape') this.close();
    }
  }

  if (!customElements.get('fusion-media-modal')) {
    customElements.define('fusion-media-modal', FusionMediaModal);
  }

  /* ======================================================================
     Add to cart
     ====================================================================== */

  /**
   * Briefly swaps the button into a confirmed "Added to cart" state — a
   * checkmark plus (where there's room) the label text — before the caller
   * moves on (usually opening the cart drawer). Purely a confidence cue; the
   * cart mutation itself has already succeeded by the time this runs.
   */
  function showAddedFeedback({ button, label, spinner, check, hideOnSuccess }) {
    return new Promise((resolve) => {
      spinner?.classList.add('hidden');
      hideOnSuccess?.classList.add('hidden');
      button.classList.add('is-added');
      if (label) {
        label.dataset.originalText = label.textContent;
        label.textContent = strings.addedToCart;
        label.classList.remove('hidden');
      }
      check?.classList.remove('hidden');
      window.setTimeout(resolve, 550);
    });
  }

  /** Shared submit handling for the product form and quick add buttons. */
  async function submitAddToCart({ form, button, spinner, label, errorEl, check, hideOnSuccess, onSuccess }) {
    const formData = new FormData(form);

    button.disabled = true;
    spinner?.classList.remove('hidden');
    label?.classList.add('hidden');
    errorEl?.classList.add('hidden');

    try {
      const items = {
        id: formData.get('id'),
        quantity: parseInt(formData.get('quantity'), 10) || 1,
      };

      const sellingPlan = formData.get('selling_plan');
      if (sellingPlan) items.selling_plan = sellingPlan;

      // Line item properties come through as properties[Name]
      const properties = {};
      formData.forEach((value, key) => {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match && value) properties[match[1]] = value;
      });
      if (Object.keys(properties).length) items.properties = properties;

      await window.Fusion.cart.add(items);

      window.Fusion.announce(strings.addedToCart);
      await showAddedFeedback({ button, label, spinner, check, hideOnSuccess });
      onSuccess?.();
    } catch (error) {
      if (errorEl) {
        errorEl.textContent = error.message;
        errorEl.classList.remove('hidden');
      }
      window.Fusion.announce(error.message, true);
    } finally {
      button.disabled = false;
      button.classList.remove('is-added');
      spinner?.classList.add('hidden');
      check?.classList.add('hidden');
      hideOnSuccess?.classList.remove('hidden');
      if (label) {
        if (label.dataset.originalText) {
          label.textContent = label.dataset.originalText;
          delete label.dataset.originalText;
        }
        label.classList.remove('hidden');
      }
    }
  }

  function openCartOrRedirect() {
    if (document.querySelector('[data-cart-drawer]')) {
      document.dispatchEvent(new CustomEvent('cart:open'));
    } else {
      window.location.href = window.Fusion.routes.cart;
    }
  }

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;

      this.form.addEventListener('submit', (event) => {
        // Without a cart drawer, let the browser post the form normally
        if (!document.querySelector('[data-cart-drawer]')) return;

        event.preventDefault();

        const button = this.querySelector('[data-add-to-cart]');
        submitAddToCart({
          form: this.form,
          button,
          spinner: this.querySelector('[data-add-to-cart-spinner]'),
          label: this.querySelector('[data-add-to-cart-text]'),
          errorEl: this.querySelector('[data-product-form-error]'),
          check: button?.querySelector('.button__check'),
          onSuccess: openCartOrRedirect,
        });
      });
    }
  }

  if (!customElements.get('product-form')) {
    customElements.define('product-form', ProductForm);
  }

  class QuickAddButton extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;

      this.form.addEventListener('submit', (event) => {
        if (!document.querySelector('[data-cart-drawer]')) return;

        event.preventDefault();

        const button = this.querySelector('[data-quick-add-submit]');
        submitAddToCart({
          form: this.form,
          button,
          spinner: this.querySelector('[data-quick-add-spinner]'),
          label: this.querySelector('[data-quick-add-text]'),
          check: button?.querySelector('.button__check, .quick-add__icon-check'),
          hideOnSuccess: button?.querySelector('.quick-add__icon-plus'),
          onSuccess: openCartOrRedirect,
        });
      });
    }
  }

  if (!customElements.get('quick-add-button')) {
    customElements.define('quick-add-button', QuickAddButton);
  }

  /* ======================================================================
     Share
     ====================================================================== */

  class ShareButton extends HTMLElement {
    connectedCallback() {
      const trigger = this.querySelector('[data-share-trigger]');
      const feedback = this.querySelector('[data-share-feedback]');
      const url = this.dataset.shareUrl;
      const title = this.dataset.shareTitle;

      trigger?.addEventListener('click', async () => {
        // Native share sheet on mobile, clipboard everywhere else
        if (navigator.share) {
          try {
            await navigator.share({ title, url });
          } catch (error) {
            // The customer dismissing the sheet is not an error
            if (error.name !== 'AbortError') console.error('[Fusion] Share failed.', error);
          }
          return;
        }

        try {
          await navigator.clipboard.writeText(url);
          if (feedback) {
            feedback.textContent = window.Fusion.strings.linkCopied;
            window.setTimeout(() => { feedback.textContent = ''; }, 3000);
          }
        } catch (error) {
          console.error('[Fusion] Copy failed.', error);
        }
      });
    }
  }

  if (!customElements.get('share-button')) {
    customElements.define('share-button', ShareButton);
  }

  /* ======================================================================
     Frequently bought together
     ====================================================================== */

  /**
   * Adds the current product plus any checked items in one request via
   * window.Fusion.cart.add (fusion-cart.js), which already accepts an
   * array. Deliberately does not compute or claim a discount — a real
   * discount, if the merchant sets one up as a Shopify automatic discount
   * targeting these products, applies naturally at checkout on its own.
   */
  class FusionBundle extends HTMLElement {
    connectedCallback() {
      this.checkboxes = Array.from(this.querySelectorAll('[data-bundle-item]'));
      this.totalEl = this.querySelector('[data-bundle-total]');
      this.button = this.querySelector('[data-bundle-add]');
      this.errorEl = this.querySelector('[data-bundle-error]');
      this.currentPrice = parseInt(this.button?.dataset.currentPrice, 10) || 0;
      this.currentVariantId = this.button?.dataset.currentVariantId;

      this.checkboxes.forEach((box) => box.addEventListener('change', () => this.updateTotal()));
      this.button?.addEventListener('click', () => this.add());

      // Checkboxes start checked, but the server-rendered total only ever
      // shows the current product's own price — correct it immediately.
      this.updateTotal();
    }

    updateTotal() {
      if (!this.totalEl) return;

      const total = this.checkboxes
        .filter((box) => box.checked)
        .reduce((sum, box) => sum + (parseInt(box.dataset.price, 10) || 0), this.currentPrice);

      this.totalEl.textContent = window.Fusion.formatMoney(total);
    }

    async add() {
      if (!this.currentVariantId || !this.button) return;

      const items = [{ id: this.currentVariantId, quantity: 1 }];
      this.checkboxes
        .filter((box) => box.checked)
        .forEach((box) => items.push({ id: box.dataset.variantId, quantity: 1 }));

      this.button.disabled = true;
      if (this.errorEl) this.errorEl.hidden = true;

      try {
        await window.Fusion.cart.add(items);
        window.Fusion.announce(strings.bundleAdded);
        openCartOrRedirect();
      } catch (error) {
        if (this.errorEl) {
          this.errorEl.textContent = error.message;
          this.errorEl.hidden = false;
        }
        window.Fusion.announce(error.message, true);
      } finally {
        this.button.disabled = false;
      }
    }
  }

  if (!customElements.get('fusion-bundle')) {
    customElements.define('fusion-bundle', FusionBundle);
  }

  /* ======================================================================
     Product card hover video
     ====================================================================== */

  /**
   * Plays the card's video only while the pointer is actually over the card,
   * rather than relying on `autoplay` — that keeps `preload="none"` honest
   * (nothing downloads until a shopper shows real interest) and means the
   * preview always restarts from the beginning on the next hover.
   */
  class ProductCardVideo extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      const card = this.closest('.product-card');
      if (!this.video || !card) return;

      card.addEventListener('mouseenter', () => {
        this.video.currentTime = 0;
        this.video.play().catch(() => {
          // Autoplay can still be blocked in some contexts — the static
          // image underneath remains visible either way, so this is silent.
        });
      });

      card.addEventListener('mouseleave', () => this.video.pause());
    }
  }

  if (!customElements.get('product-card-video')) {
    customElements.define('product-card-video', ProductCardVideo);
  }

  /* ======================================================================
     Product card interactive swatches
     ====================================================================== */

  /**
   * Opt-in enhancement (settings.product_card_swatch_select) for cards whose
   * variants are driven entirely by one swatch option — almost always
   * Color. Hovering or focusing a swatch previews that variant's own photo
   * on the card; clicking selects it, so the card's own Add to cart / Buy it
   * now buttons act on that exact variant without leaving the grid.
   *
   * Only rendered by snippets/product-card.liquid when the product has a
   * single option and every value has both a swatch and a real variant (see
   * `swatch_buyable` there) — a product with a second option (e.g. Size)
   * still needs a full pick before a variant is known, so it keeps the
   * existing "Choose options" link instead of pretending a color alone is
   * enough.
   */
  class ProductCardSwatches extends HTMLElement {
    connectedCallback() {
      this.card = this.closest('.product-card');
      this.image = this.card?.querySelector('.product-card__image--primary');
      this.buttons = Array.from(this.querySelectorAll('[data-swatch-variant]'));
      this.selected = this.buttons.find((button) => button.classList.contains('is-selected')) || null;
      this.baseSrc = this.selected?.dataset.swatchImage || this.image?.src;

      this.buttons.forEach((button) => {
        button.addEventListener('mouseenter', () => this.preview(button));
        button.addEventListener('mouseleave', () => this.restore());
        button.addEventListener('focus', () => this.preview(button));
        button.addEventListener('blur', () => this.restore());
        button.addEventListener('click', (event) => {
          event.preventDefault();
          this.select(button);
        });
      });
    }

    /** Temporary hover/focus preview — reverts on mouseleave/blur unless clicked. */
    preview(button) {
      const src = button.dataset.swatchImage;
      if (this.image && src) this.image.src = src;
    }

    restore() {
      if (!this.image || !this.baseSrc) return;
      this.image.src = this.baseSrc;
    }

    /** A real click/tap confirms the choice — this is what buy buttons act on. */
    select(button) {
      if (button.dataset.swatchAvailable === 'false') return;

      this.selected = button;
      this.buttons.forEach((b) => b.classList.toggle('is-selected', b === button));

      if (button.dataset.swatchImage) {
        this.baseSrc = button.dataset.swatchImage;
        if (this.image) this.image.src = button.dataset.swatchImage;
      }

      const variantId = button.dataset.swatchVariant;
      if (!variantId) return;

      // The card's own quick-add / quick-buy forms mark their hidden variant
      // id input with this attribute (see snippets/quick-add.liquid and
      // snippets/quick-buy.liquid) — updating its value here is the only
      // wiring needed, since both forms read it fresh at submit time.
      this.card?.querySelectorAll('[data-variant-id-input]').forEach((input) => {
        input.value = variantId;
      });
    }
  }

  if (!customElements.get('product-card-swatches')) {
    customElements.define('product-card-swatches', ProductCardSwatches);
  }

  /* ======================================================================
     Recommendations
     ====================================================================== */

  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      // Already rendered by the section fetch itself
      if (this.querySelector('.recommendations__grid')) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            observer.unobserve(this);
            this.load();
          });
        },
        { rootMargin: '0px 0px 400px 0px' }
      );

      observer.observe(this);
    }

    async load() {
      try {
        const response = await fetch(this.dataset.url);
        if (!response.ok) throw new Error(`Recommendations failed (${response.status})`);

        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const fresh = html.querySelector('product-recommendations');

        if (!fresh || !fresh.innerHTML.trim()) {
          // No recommendations for this product — remove the empty section
          this.remove();
          return;
        }

        this.innerHTML = fresh.innerHTML;
        window.Fusion.initScrollAnimations();
      } catch (error) {
        console.error('[Fusion] Recommendations failed to load.', error);
        this.remove();
      }
    }
  }

  if (!customElements.get('product-recommendations')) {
    customElements.define('product-recommendations', ProductRecommendations);
  }

})();
